/* Generic Partner Portal Shell (Phase PP-2/3/4 · Feb 2026).
   Re-usable, theme-able sidebar shell shared by lender / insurance /
   carrier portals. Banking has its own variant (BankingPortalShell.jsx)
   that was shipped first; this component covers the rest.

   Props:
     • role        — 'lender_partner' | 'insurance_partner' | 'carrier_partner'
     • portalName  — display label (e.g. "Lender")
     • portalIcon  — lucide icon component for the header glyph
     • theme       — Tailwind colour family (e.g. 'violet')
     • nav         — array of {to, label, icon, end}
     • whoamiPath  — backend endpoint that returns whoami payload
     • logoutTo    — route to push on sign-out
*/
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { LogOut, Building2, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";

// Static class map — Tailwind JIT requires literal class names in source.
const THEME_CLASSES = {
  sky:     { bgIcon: "bg-sky-500/20",     textIcon: "text-sky-300",     bgActive: "bg-sky-500/15",     textActive: "text-sky-100" },
  violet:  { bgIcon: "bg-violet-500/20",  textIcon: "text-violet-300",  bgActive: "bg-violet-500/15",  textActive: "text-violet-100" },
  emerald: { bgIcon: "bg-emerald-500/20", textIcon: "text-emerald-300", bgActive: "bg-emerald-500/15", textActive: "text-emerald-100" },
  amber:   { bgIcon: "bg-amber-500/20",   textIcon: "text-amber-300",   bgActive: "bg-amber-500/15",   textActive: "text-amber-100" },
  slate:   { bgIcon: "bg-slate-500/20",   textIcon: "text-slate-300",   bgActive: "bg-slate-500/15",   textActive: "text-slate-100" },
};

export default function PartnerPortalShell({
  role, portalName, portalIcon: PortalIcon, theme = "slate",
  nav = [], whoamiPath, logoutTo,
}) {
  const { user, logout } = useAuth();
  const navigateTo = useNavigate();
  const [meta, setMeta] = useState(null);
  const tc = THEME_CLASSES[theme] || THEME_CLASSES.slate;

  useEffect(() => {
    if (!whoamiPath) return;
    api.get(whoamiPath).then(r => setMeta(r.data)).catch(() => {});
  }, [whoamiPath]);

  const doLogout = async () => {
    try { await logout(); } finally { navigateTo(logoutTo, { replace: true }); }
  };

  return (
    <div data-testid={`${role}-portal-shell`} className="min-h-screen bg-slate-50 flex">
      <aside className="w-60 bg-slate-900 text-slate-100 flex flex-col">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-md ${tc.bgIcon} grid place-items-center`}>
              <PortalIcon className={`w-4 h-4 ${tc.textIcon}`} />
            </div>
            <div>
              <div className="text-sm font-semibold">verqo · {portalName}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Partner Portal</div>
            </div>
          </div>
        </div>

        <nav className={`flex-1 py-3 px-2 space-y-0.5`} data-testid={`${role}-sidebar`}>
          {nav.map(item => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`${role}-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition ${
                    isActive
                      ? `${tc.bgActive} ${tc.textActive}`
                      : "text-slate-300 hover:bg-slate-800/60 hover:text-white"
                  }`
                }
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-3 py-3 border-t border-slate-800 space-y-1.5">
          {meta && (
            <div className="px-2 py-2 rounded-md bg-slate-800/40 text-xs text-slate-300 leading-snug" data-testid={`${role}-org-info`}>
              <div className="flex items-center gap-1.5 text-slate-200 font-medium">
                <Building2 className="w-3.5 h-3.5" /> {meta.partner_org_name || "Unattached"}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5 truncate">{user?.email}</div>
              {meta.partner_org_status === "active" && (
                <div className="text-[10px] text-emerald-300 mt-1 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Org active
                </div>
              )}
            </div>
          )}
          <Button
            variant="ghost"
            onClick={doLogout}
            data-testid={`${role}-logout-btn`}
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
